import { useState, useCallback } from "react";
import axios, {
  type AxiosRequestConfig,
  type Method,
  type AxiosError,
} from "axios";
import { useLoader } from "@/context/loader-context/LoaderContext";
import { getIdentificationData } from "@/lib/identification.utils";
import { BASE_URL_API } from "@/lib/api";
interface AxiosParams {
  url: string;
  method?: Method;
  body?: any;
  headers?: Record<string, string>;
}

interface ErrorType {
  message: string;
  status?: string | number;
}

type Params = Record<string, string | number>;

export const useAxios = <T = any>() => {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<{
    status?: string | number;
    message: string;
  } | null>(null);
  const { showLoader, hideLoader } = useLoader();

  const buildRoute = useCallback((baseRoute: string, params: Params) => {
    return baseRoute
      .replace(/\{(\w+)\}/g, (_, key) => {
        if (params[key] !== undefined && params[key] !== null) {
          return encodeURIComponent(params[key]);
        }
        return "";
      })
      .replace(/\/+/g, "/");
  }, []);

  const request = useCallback(
    async ({
      method = "GET",
      url,
      body = undefined,
    }: AxiosParams): Promise<T | null> => {
      setError(null);
      showLoader();

      const { token } = getIdentificationData();

      const headers = {
        "Content-Type": "application/json",
        Authorization: token,
      };

      const baseURL = BASE_URL_API;

      const config: AxiosRequestConfig = {
        method,
        url,
        headers,
        baseURL,
      };

      if (body) {
        config.data = JSON.stringify(body);
      }

      try {
        const response = await axios(config);
        setData(response.data);
        hideLoader();
        return response.data;
      } catch (err: any) {
        const axiosError = err as AxiosError;
        if (axiosError.response?.status === 401) {
          window.location.href = "/unauthorized";
        }
        const errorObject: ErrorType = {
          status: axiosError.response?.status,
          message: axiosError.message || "Unknown error",
        };
        setError(errorObject);
        hideLoader();
        throw errorObject;
      }
    },
    [showLoader, hideLoader],
  );

  const requestAll = useCallback(
    async (urls: string[]): Promise<T[]> => {
      setError(null);
      showLoader();

      const { token } = getIdentificationData();

      const headers = {
        "Content-Type": "application/json",
        Authorization: token,
      };

      const baseURL = BASE_URL_API;

      try {
        const requests = urls.map((url) =>
          axios<T>({ method: "GET", url, headers, baseURL }),
        );
        const responses = await Promise.all(requests);
        const dataArray = responses.map((response) => response.data);
        setData(dataArray as unknown as T);
        hideLoader();
        return dataArray;
      } catch (err: any) {
        const axiosError = err as AxiosError;
        const errorObject: ErrorType = {
          status: axiosError.response?.status,
          message: axiosError.message || "Unknown error",
        };
        setError(errorObject);
        hideLoader();
        throw errorObject;
      }
    },
    [showLoader, hideLoader],
  );

  return { data, error, request, requestAll, buildRoute };
};
